/**
 * "Link not working?" (core linkreports.ts; services/api/src/linkreports/). One tap sends: the address, the page it was on, the
 * link's data key if the page knows one (data-link-key="nv/snhd/permit.renew"), the kind of link (data-link-kind), the state and
 * county of the place, the time (server), and the person's id only when signed in. An optional one-line note can follow.
 *
 * Two ways in, both quiet:
 *  - <ReportLinkButton url …/> a small text link under a contact directory entry or a list of sources;
 *  - <LinkReportHost/> (mounted once in App): after a tap on ANY outside link (an absolute http(s) address on another host,
 *    not marked data-no-report) a small "Did that link open?" chip appears when the person is back looking at the page, for
 *    12 seconds, and goes. Internal links, tel:, sms: and mailto: never get it.
 */
// element: report-link · journey: a link dies → one tap tells the team · tokens: .link-report* (styles.css), icon flag
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isExternalHref, isReportLinkKind, LINK_THANKS, NOTE_LIMIT, placeState, type ReportLinkKind } from "@logwave/core";
import { Icon } from "./Icons.tsx";
import { useApiMaybe, useSessionMaybe } from "../session.tsx";

/** Where the person is and who they are, for the report. */
function useReportContext() {
  const s = useSessionMaybe();
  // The address bar, not the router: this also draws where there is no router (a rule line on a bare page). The app's own /app prefix is not part of the screen's name.
  const page = typeof window === "undefined" ? "" : window.location.pathname.replace(/^\/app(?=\/)/, "");
  return {
    page,
    ...(s ? { state: placeState(s.location, s.org), orgId: s.org.id, ...(s.location.county ? { county: s.location.county } : {}) } : {}),
  };
}

export interface ReportTarget { url: string; kind?: ReportLinkKind; linkKey?: string }

/** The flow: the one tap, then thanks with an optional note. */
export function ReportFlow({ url, kind = "other", linkKey, label = "Link not working?", quiet = true, onSent }: ReportTarget & { label?: string; quiet?: boolean; onSent?: () => void }) {
  const api = useApiMaybe();
  const ctx = useReportContext();
  const [phase, setPhase] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [note, setNote] = useState("");
  const [noted, setNoted] = useState(false);
  const send = useCallback(async (extra?: { note: string }) => {
    if (!api) return;
    setPhase("sending");
    try {
      await api.reportLink({ url, kind, ...(linkKey ? { key: linkKey } : {}), ...ctx, ...(extra ? { note: extra.note, followUp: true } : {}) });
      setPhase("sent");
      if (extra) setNoted(true); else onSent?.();
    } catch { setPhase(extra ? "sent" : "failed"); }
  }, [api, url, kind, linkKey, ctx, onSent]);
  if (!api) return null;
  if (phase === "sent") {
    return (
      <span className="link-report-done" role="status">
        <span className="link-report-thanks"><Icon name="check" size={16} className="link-report-check" /> {noted ? "Added. " : ""}{LINK_THANKS}</span>
        {!noted && (
          <form className="link-report-note" onSubmit={(e) => { e.preventDefault(); if (note.trim()) void send({ note: note.trim() }); }}>
            <label className="sr-only" htmlFor={`lrn-${url}`}>Add a few words (optional)</label>
            <input id={`lrn-${url}`} value={note} maxLength={NOTE_LIMIT} onChange={(e) => setNote(e.target.value)} placeholder="Add a few words, like: goes to a 404" autoComplete="off" />
            <button type="submit" className="link-report-btn" disabled={!note.trim()}>Add</button>
          </form>
        )}
      </span>
    );
  }
  return (
    <span className="link-report">
      <button type="button" className={`link-report-btn${quiet ? " is-quiet" : ""}`} onClick={() => void send()} disabled={phase === "sending"} data-no-report>
        <Icon name="flag" size={15} /> {phase === "sending" ? "Sending…" : label}
      </button>
      {phase === "failed" && <span className="caption" role="alert"> We couldn't send that. Tap to try again.</span>}
    </span>
  );
}

/** A small text link under a contact entry or a list of sources. */
export function ReportLinkButton(p: ReportTarget & { label?: string }) {
  return <span className="link-report-row" data-no-report><ReportFlow {...p} /></span>;
}

/** What a tapped anchor says about itself. */
function targetOf(a: HTMLAnchorElement): ReportTarget & { page?: string } | null {
  if (a.closest("[data-no-report]") || a.hasAttribute("data-no-report")) return null;
  const href = a.href;
  if (!isExternalHref(href, window.location.host)) return null;
  const kindRaw = a.dataset.linkKind ?? a.closest<HTMLElement>("[data-link-kind]")?.dataset.linkKind;
  const key = a.dataset.linkKey ?? a.closest<HTMLElement>("[data-link-key]")?.dataset.linkKey;
  return { url: href, kind: isReportLinkKind(kindRaw) ? kindRaw : "other", ...(key ? { linkKey: key } : {}) };
}

const SHOW_FOR_MS = 12_000;

/** Mount once. After a tap on an outside link, a quiet "Link not working?" when the person is back on the page. */
export function LinkReportHost({ delayMs = 2500 }: { delayMs?: number }) {
  const api = useApiMaybe();
  const loc = useLocation();
  const [shown, setShown] = useState<ReportTarget | null>(null);
  const pending = useRef<ReportTarget | null>(null);
  const timers = useRef<{ show?: ReturnType<typeof setTimeout>; hide?: ReturnType<typeof setTimeout> }>({});
  const show = useCallback(() => {
    if (!pending.current) return;
    setShown(pending.current); pending.current = null;
    clearTimeout(timers.current.hide);
    timers.current.hide = setTimeout(() => setShown(null), SHOW_FOR_MS);
  }, []);
  useEffect(() => {
    if (!api) return;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      const t = a ? targetOf(a) : null;
      if (!t) return;
      pending.current = t; setShown(null);
      clearTimeout(timers.current.show);
      timers.current.show = setTimeout(() => { if (document.visibilityState === "visible") show(); }, delayMs);
    };
    // The link opened in another tab: the chip waits until the person is back.
    const onVisible = () => { if (document.visibilityState === "visible" && pending.current) show(); };
    document.addEventListener("click", onClick, true);
    document.addEventListener("visibilitychange", onVisible);
    return () => { document.removeEventListener("click", onClick, true); document.removeEventListener("visibilitychange", onVisible); clearTimeout(timers.current.show); clearTimeout(timers.current.hide); };
  }, [api, delayMs, show]);
  // Moving on to another screen: the chip is about the last link, so it goes.
  useEffect(() => { setShown(null); pending.current = null; }, [loc.pathname]);
  if (!api || !shown) return null;
  return (
    <div className="link-report-chip" role="region" aria-label="Did that link open?" data-no-report>
      <span className="link-report-q">Did that link open?</span>
      <ReportFlow key={shown.url} {...shown} />
      <button type="button" className="link-report-x" aria-label="Dismiss" onClick={() => setShown(null)}><Icon name="x" size={16} /></button>
    </div>
  );
}
